/**
 * Note-template adapter for the generic ResourceCatalog (GDN-2482) — the
 * second adapter on the seam GDN-2335 built (appointment types were the first).
 *
 * Normalizes a catalog-bootstrap row into a resource-agnostic CatalogRow plus
 * the adapter-only metadata the detail panel and verb-routing need. The catalog
 * itself never learns note-template vocabulary; it all arrives here.
 */
import type {
  CatalogLabels,
  CatalogRow,
} from '@components/ui/catalog/ResourceCatalog/ResourceCatalog.types';

// Type-only import via the direct /fixtures path — never the feature barrel
// (it auto-registers the templates feature at module load, which breaks
// Storybook; see web-app CLAUDE.md).
import type {
  TemplateCatalogAction,
  TemplateCatalogBootstrapRow,
} from '../fixtures/types';

/** One note template, catalog-ready: the row plus adapter-only metadata. */
export interface TemplateCatalogEntry {
  row: CatalogRow;
  /** Which backend verb the row's archive/restore maps to (archive vs de-adopt). */
  action: TemplateCatalogAction;
  /** Linked appointment-type names, for the expandable detail. */
  linkedTypeNames: string[];
  /** True when this personal row is a public template a clinician adopted. */
  isPublicAdoption: boolean;
}

/** "3 appointment types" / "1 appointment type" / null when unlinked. */
const toLinkedLabel = (count: number): string | null => {
  if (count <= 0) return null;
  return `${count} appointment type${count === 1 ? '' : 's'}`;
};

export const toCatalogEntry = (
  r: TemplateCatalogBootstrapRow
): TemplateCatalogEntry => {
  // org → 'shared' (practice-wide). user + public-adoption → 'personal' (a
  // clinician's row). Public templates have no first-class 'public' row scope;
  // they only surface as a clinician's adoption, hence personal.
  const scope: CatalogRow['scope'] =
    r.owner_scope === 'org' ? 'shared' : 'personal';

  return {
    row: {
      id: r.row_id,
      name: r.title,
      color: null, // note templates have no swatch color
      scope,
      ownerLabel: scope === 'personal' ? (r.provider ?? null) : null,
      usage: r.usage,
      lastUsedAt: r.last_used_at ?? null,
      // The linked relationship is inverted vs the type catalog: here we show
      // the appointment types that USE this template, as a count string (names
      // live in the detail). No linked scope chip — hence linkedScope null.
      linkedLabel: toLinkedLabel(r.linked_type_count),
      linkedScope: null,
      archived: r.archived,
      // Public-adoption rows detach from a clinician's list (Remove/Re-add),
      // not soft-delete; org/user template rows archive.
      actionKind: r.action === 'deadopt' ? 'remove' : 'archive',
      // Provenance chip: a personal row that's a public template a clinician adopted.
      badge:
        r.row_kind === 'adoption' ? { label: 'Public', color: 'info' } : null,
    },
    action: r.action,
    linkedTypeNames: r.linked_type_names,
    isPublicAdoption: r.row_kind === 'adoption',
  };
};

export const toCatalogEntries = (
  rows: TemplateCatalogBootstrapRow[]
): TemplateCatalogEntry[] => rows.map(toCatalogEntry);

/** The catalog's display vocabulary for the note-template resource. */
export const TEMPLATE_CATALOG_LABELS: CatalogLabels = {
  noun: 'note template',
  nounPlural: 'note templates',
  sharedScopeLabel: 'Practice-wide',
  personalScopeLabel: 'Personal',
  publicScopeLabel: 'Public',
  // Templates invert the link: the column shows the appointment types that
  // USE this template, not a single linked template.
  linkedColumnHeader: 'Used by',
  ownerFilterLabel: 'Clinician',
  expandDetailTooltip: 'Show details',
  collapseDetailTooltip: 'Hide details',
  archiveExplanation:
    'Archived note templates stay on past notes — they just stop appearing ' +
    'when charting. You can restore them anytime.',
  removeExplanation:
    "Removing a public template from a clinician's list doesn't delete it — " +
    'it just stops appearing when they chart. You can re-add it anytime.',
};
